import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Chip, Button, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress, Alert, Snackbar, Grid, LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Block as VoidIcon,
  CreditCard as CreditNoteIcon,
  Flag as DisputeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Send as SubmitIcon,
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  Payments as PaymentPlanIcon,
  NoteAlt as NoteAltIcon,
} from '@mui/icons-material';
import OrderWorkflowNotesDialog from '../components/OrderWorkflowNotesDialog';
import { useAuth } from '../context/AuthContext';
import config from '../config';

// ─── Status chip helper ───────────────────────────────────────────────────────

// CRM status config — keyed on the normalised crmStatus field from the server
const CRM_STATUS_CFG = {
  UNPAID:       { label: 'Unpaid',        color: 'warning', icon: <WarningIcon     sx={{ fontSize: 14 }} />, filled: false },
  OVERDUE:      { label: 'Overdue',       color: 'error',   icon: <WarningIcon     sx={{ fontSize: 14 }} />, filled: true  },
  PAID:         { label: 'Paid',          color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 14 }} />, filled: false },
  VOIDED:       { label: 'Voided',        color: 'default', icon: <CancelIcon      sx={{ fontSize: 14 }} />, filled: false },
  PAYMENT_PLAN: { label: 'Payment Plan',  color: 'info',    icon: <PaymentPlanIcon sx={{ fontSize: 14 }} />, filled: false },
};

function StatusChip({ crmStatus }) {
  const cfg = CRM_STATUS_CFG[crmStatus] || { label: crmStatus || '—', color: 'default', icon: null, filled: false };
  return (
    <Chip
      size="small"
      icon={cfg.icon}
      label={cfg.label}
      color={cfg.color}
      variant={cfg.filled ? 'filled' : 'outlined'}
      sx={{ fontWeight: 600, fontSize: 11 }}
    />
  );
}

// ─── Leave type labels ────────────────────────────────────────────────────────

const LEAVE_LABELS = {
  annual: 'Annual Leave',
  sick: 'Sick Day',
  public_holiday: 'Public Holiday',
  other: 'Other',
};

// ─── Tab panel ────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Finance() {
  const { getToken, isAdmin } = useAuth();
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showMsg = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // ── Invoices state ──────────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const [actionDialog, setActionDialog] = useState({ open: false, type: '', order: null });
  const [actionReason, setActionReason] = useState('');
  const [notesDialog, setNotesDialog] = useState({ open: false, entityType: 'order', entityId: null, title: '', notes: [] });

  // ── Unpaid Orders state ─────────────────────────────────────────────────────
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [unpaidLoading, setUnpaidLoading] = useState(false);
  const [unpaidConfirm, setUnpaidConfirm] = useState({ open: false, order: null, action: null });
  const [paymentPlanConfirm, setPaymentPlanConfirm] = useState({ open: false, order: null });

  // ── Payroll — internal preview ───────────────────────────────────────────────
  const [weeklyPay, setWeeklyPay] = useState(null);
  const [weeklyPayLoading, setWeeklyPayLoading] = useState(false);
  const [commissions, setCommissions] = useState(null);
  const [commissionsLoading, setCommissionsLoading] = useState(false);

  // ── Payroll — Xero pay run ───────────────────────────────────────────────────
  const [payPeriodStart, setPayPeriodStart] = useState(getMonday());
  const [payPeriodEnd, setPayPeriodEnd] = useState(getSunday());
  const [payType, setPayType] = useState('weekly');
  const [payPreview, setPayPreview] = useState(null);
  const [payPreviewLoading, setPayPreviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [payHistory, setPayHistory] = useState([]);
  const [payHistoryLoading, setPayHistoryLoading] = useState(false);

  const [pendingLeave, setPendingLeave] = useState([]);
  const [pendingLeaveLoading, setPendingLeaveLoading] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(getSunday());

  // ── Fetch invoices ──────────────────────────────────────────────────────────

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (invoiceSearch) params.set('search', invoiceSearch);
      if (invoiceStatusFilter) params.set('status', invoiceStatusFilter);
      const resp = await fetch(`${config.apiUrl}/api/xero/invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success) setInvoices(data.invoices);
    } catch (e) {
      showMsg('Failed to load invoices', 'error');
    } finally {
      setInvoicesLoading(false);
    }
  }, [getToken, invoiceSearch, invoiceStatusFilter]);

  useEffect(() => {
    if (tab === 0) fetchInvoices();
  }, [tab, fetchInvoices]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = await getToken();
      await fetch(`${config.apiUrl}/api/xero/refresh-invoices`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchInvoices();
      showMsg('Invoice statuses refreshed from Xero');
    } catch (e) {
      showMsg('Refresh failed', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // ── Invoice notes ────────────────────────────────────────────────────────────

  // Map Finance orderType strings to the notes API entity type
  const getNotesEntityType = (orderType) => {
    if (orderType === 'school')     return 'school_order';
    if (orderType === 'stationery') return 'stationery_order';
    return 'order'; // 'ad' or anything else → ad_orders
  };

  const openNotesDialog = async (id, orderType, label) => {
    const entityType = getNotesEntityType(orderType);
    try {
      const token = await getToken();
      const res = await fetch(`${config.apiUrl}/api/notes/${entityType}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.ok ? await res.json() : null;
      const existingNotes = data?.success ? (data.notes || []) : [];
      setNotesDialog({ open: true, entityType, entityId: id, title: `Invoice Notes — ${label}`, notes: existingNotes });
    } catch {
      setNotesDialog({ open: true, entityType, entityId: id, title: `Invoice Notes — ${label}`, notes: [] });
    }
  };

  const closeNotesDialog = () => setNotesDialog(prev => ({ ...prev, open: false }));

  // ── Invoice actions ─────────────────────────────────────────────────────────

  const openAction = (type, order) => {
    setActionDialog({ open: true, type, order });
    setActionReason('');
  };

  const closeAction = () => setActionDialog({ open: false, type: '', order: null });

  const submitAction = async () => {
    const { type, order } = actionDialog;
    const token = await getToken();
    const endpoint = type === 'void'
      ? `/api/xero/void-invoice/${order.id}`
      : type === 'credit'
        ? `/api/xero/credit-note/${order.id}`
        : `/api/xero/dispute/${order.id}`;

    try {
      const resp = await fetch(`${config.apiUrl}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderType: order.orderType, reason: actionReason }),
      });
      const data = await resp.json();
      if (data.success) {
        showMsg(data.message);
        closeAction();
        fetchInvoices();
      } else {
        showMsg(data.message, 'error');
      }
    } catch (e) {
      showMsg('Action failed', 'error');
    }
  };

  // ── Payroll — internal fetches ───────────────────────────────────────────────

  const fetchWeeklyPay = useCallback(async () => {
    setWeeklyPayLoading(true);
    try {
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/payroll/weekly`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success) setWeeklyPay(data);
      else showMsg(data.message || 'Failed to load weekly pay', 'error');
    } catch (e) {
      showMsg('Failed to load weekly pay data', 'error');
    } finally {
      setWeeklyPayLoading(false);
    }
  }, [getToken]);

  const fetchCommissions = useCallback(async () => {
    setCommissionsLoading(true);
    try {
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/payroll/commissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success) setCommissions(data);
      else showMsg(data.message || 'Failed to load commissions', 'error');
    } catch (e) {
      showMsg('Failed to load commission data', 'error');
    } finally {
      setCommissionsLoading(false);
    }
  }, [getToken]);

  // ── Payroll — Xero pay run ────────────────────────────────────────────────────

  const fetchPayPreview = async () => {
    setPayPreviewLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ periodStart: payPeriodStart, periodEnd: payPeriodEnd, type: payType });
      const resp = await fetch(`${config.apiUrl}/api/xero/payroll/preview?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success) setPayPreview(data);
      else showMsg(data.message, 'error');
    } catch (e) {
      showMsg('Failed to load pay preview', 'error');
    } finally {
      setPayPreviewLoading(false);
    }
  };

  const fetchPayHistory = useCallback(async () => {
    setPayHistoryLoading(true);
    try {
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/xero/payroll/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success) setPayHistory(data.history);
    } catch (e) {
      console.warn('Could not fetch pay history', e);
    } finally {
      setPayHistoryLoading(false);
    }
  }, [getToken]);

  const fetchPendingLeave = useCallback(async () => {
    if (!isAdmin) return;
    setPendingLeaveLoading(true);
    try {
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/leave/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success) setPendingLeave(data.pending);
    } catch (e) {
      console.warn('Could not fetch pending leave', e);
    } finally {
      setPendingLeaveLoading(false);
    }
  }, [getToken, isAdmin]);

  useEffect(() => {
    if (tab === 1) {
      fetchWeeklyPay();
      fetchCommissions();
      fetchPayHistory();
      fetchPendingLeave();
    }
  }, [tab, fetchWeeklyPay, fetchCommissions, fetchPayHistory, fetchPendingLeave]);

  const handleLeaveAction = async (id, action) => {
    const token = await getToken();
    try {
      await fetch(`${config.apiUrl}/api/leave/${id}/${action}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPendingLeave();
      showMsg(`Leave request ${action}d`);
    } catch (e) {
      showMsg('Action failed', 'error');
    }
  };

  const handleSubmitPayRun = async () => {
    setSubmitLoading(true);
    try {
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/xero/payroll/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          periodStart: payPeriodStart,
          periodEnd: payPeriodEnd,
          type: payType,
          paymentDate,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        showMsg(`Pay run submitted to Xero! Total: $${data.totalGross?.toFixed(2)}`);
        setConfirmSubmitOpen(false);
        setPayPreview(null);
        fetchPayHistory();
      } else {
        showMsg(data.message, 'error');
      }
    } catch (e) {
      showMsg('Submit failed', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  // ── Unpaid Orders: fetch and actions ─────────────────────────────────────────

  const fetchUnpaidOrders = useCallback(async () => {
    setUnpaidLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${config.apiUrl}/api/ad-orders/unpaid`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setUnpaidOrders(data.data || []);
      else showMsg(data.message || 'Failed to load unpaid orders', 'error');
    } catch (err) {
      showMsg('Failed to load unpaid orders', 'error');
    } finally {
      setUnpaidLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (tab === 2) fetchUnpaidOrders();
  }, [tab, fetchUnpaidOrders]);

  const handleUnpaidAction = async () => {
    const { order, action } = unpaidConfirm;
    if (!order || !action) return;
    try {
      const token = await getToken();
      const { fetchCSRFToken } = await import('../utils/csrfProtection');
      const csrf = await fetchCSRFToken();
      const newStatus = action === 'paid' ? 'Paid' : 'Cancelled';
      const res = await fetch(`${config.apiUrl}/api/ad-orders/${order.id}/status`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      showMsg(`Order #${order.id} marked as ${newStatus}`);
      setUnpaidOrders(prev => prev.filter(o => o.id !== order.id));
    } catch (err) {
      showMsg(err.message || 'Action failed', 'error');
    } finally {
      setUnpaidConfirm({ open: false, order: null, action: null });
    }
  };

  const handlePaymentPlan = async () => {
    const { order } = paymentPlanConfirm;
    if (!order) return;
    try {
      const token = await getToken();
      const res = await fetch(`${config.apiUrl}/api/xero/payment-plan/${order.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderType: 'ad' }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      showMsg(`Order #${order.id} marked as Payment Plan`);
    } catch (err) {
      showMsg(err.message || 'Action failed', 'error');
    } finally {
      setPaymentPlanConfirm({ open: false, order: null });
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Finance
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Invoices" />
          <Tab label="Payroll" />
          <Tab label="Unpaid Orders" />
        </Tabs>

        {/* ── Invoices Tab ──────────────────────────────────────────────────── */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Search & filter bar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search org name or invoice ID…"
                value={invoiceSearch}
                onChange={e => setInvoiceSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchInvoices()}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                sx={{ minWidth: 260 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={invoiceStatusFilter}
                  label="Status Filter"
                  onChange={e => setInvoiceStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="voided">Voided</MenuItem>
                  <MenuItem value="payment_plan">Payment Plan</MenuItem>
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                onClick={fetchInvoices}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
              {isAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRefresh}
                  startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
                  disabled={refreshing}
                  sx={{ ml: 'auto' }}
                >
                  Refresh from Xero
                </Button>
              )}
            </Box>

            {invoicesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organisation</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount (inc GST)</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No invoices found
                        </TableCell>
                      </TableRow>
                    ) : invoices.map(inv => (
                      <TableRow key={`${inv.orderType}-${inv.id}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{inv.orgName || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {inv.xero_invoice_id?.substring(0, 8)}…
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={inv.orderType?.toUpperCase()} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            ${parseFloat(inv.totalPrice || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {inv.xero_invoice_due_date
                            ? new Date(inv.xero_invoice_due_date).toLocaleDateString('en-AU')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <StatusChip crmStatus={inv.crmStatus} />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {inv.xero_invoice_url && (
                              <Tooltip title="View Invoice in Xero">
                                <IconButton size="small" component="a" href={inv.xero_invoice_url} target="_blank" rel="noopener noreferrer">
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Invoice Notes">
                              <IconButton size="small" color="primary" onClick={() => openNotesDialog(inv.id, inv.orderType, inv.orgName || `Order #${inv.id}`)}>
                                <NoteAltIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isAdmin && inv.xero_invoice_status !== 'PAID' && inv.xero_invoice_status !== 'VOIDED' && (
                              <>
                                <Tooltip title="Void Invoice">
                                  <IconButton size="small" color="error" onClick={() => openAction('void', inv)}>
                                    <VoidIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Credit Note">
                                  <IconButton size="small" color="warning" onClick={() => openAction('credit', inv)}>
                                    <CreditNoteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Flag Dispute">
                                  <IconButton size="small" onClick={() => openAction('dispute', inv)}>
                                    <DisputeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* ── Payroll Tab ───────────────────────────────────────────────────── */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ px: 2, pb: 3 }}>

            {/* ── Month-end alert ──────────────────────────────────────────── */}
            {weeklyPay?.is_month_end && (
              <Alert severity="warning" icon={<TrendingUpIcon />} sx={{ mb: 3 }}>
                <strong>Month-end pay period:</strong> {new Date(weeklyPay.month_end_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long' })} falls in this week —
                monthly commissions are due this pay run.
              </Alert>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                SECTION 1: WEEKLY PAY
            ══════════════════════════════════════════════════════════════════ */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Weekly Pay</Typography>
                {weeklyPay && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(weeklyPay.week_start).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {' – '}
                    {new Date(weeklyPay.week_end).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </Typography>
                )}
              </Box>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={fetchWeeklyPay} disabled={weeklyPayLoading}>
                  {weeklyPayLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>

            {weeklyPayLoading && !weeklyPay ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Employment</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Sched. Hours</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Rate</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Expected Pay</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(weeklyPay?.users || []).map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{u.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={u.role}
                            variant="outlined"
                            color={u.role === 'sales' ? 'primary' : u.role === 'admin' ? 'error' : 'default'}
                            sx={{ textTransform: 'capitalize', fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={u.employment_type?.replace(/_/g, ' ')}
                            variant="outlined"
                            sx={{ textTransform: 'capitalize', fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {u.employment_type === 'salaried'
                            ? '—'
                            : u.scheduled_hours > 0
                              ? `${u.scheduled_hours.toFixed(1)} hrs`
                              : <Typography variant="caption" color="text.secondary">No schedule</Typography>
                          }
                        </TableCell>
                        <TableCell align="right">
                          {u.base_rate > 0
                            ? u.employment_type === 'salaried'
                              ? `$${u.base_rate.toFixed(2)}/wk`
                              : `$${u.base_rate.toFixed(2)}/hr`
                            : '—'
                          }
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={u.expected_pay > 0 ? 'text.primary' : 'text.secondary'}
                          >
                            {u.expected_pay > 0 ? `$${u.expected_pay.toFixed(2)}` : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}

                    {weeklyPay && (
                      <TableRow sx={{ bgcolor: 'primary.50' }}>
                        <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>
                          Total Expected This Week
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.95rem' }}>
                          ${weeklyPay.total_expected?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                SECTION 2: MONTHLY COMMISSIONS
            ══════════════════════════════════════════════════════════════════ */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Monthly Commissions</Typography>
                {commissions && (
                  <Typography variant="caption" color="text.secondary">
                    {commissions.month_label} · Sales users only
                    {commissions.bonus_amount > 0 && ` · Target bonus: $${commissions.bonus_amount.toFixed(2)}`}
                  </Typography>
                )}
              </Box>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={fetchCommissions} disabled={commissionsLoading}>
                  {commissionsLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>

            {commissionsLoading && !commissions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !commissions || commissions.users?.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>No sales users with commission targets set up yet.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Sales Person</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Monthly Target</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Invoiced This Month</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Progress</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Bonus</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Overage (20%)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total Commission</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commissions.users.map(u => (
                      <TableRow key={u.id} hover sx={u.target_reached ? { bgcolor: 'success.50' } : {}}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {u.target_reached && (
                              <Tooltip title="Target reached!">
                                <TrophyIcon fontSize="small" sx={{ color: 'warning.main' }} />
                              </Tooltip>
                            )}
                            <Typography variant="body2" fontWeight={500}>{u.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {u.monthly_target > 0
                            ? `$${u.monthly_target.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`
                            : <Typography variant="caption" color="text.secondary">Not set</Typography>
                          }
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={u.target_reached ? 'success.main' : 'text.primary'}
                          >
                            ${u.invoiced_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {u.target_progress !== null ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={u.target_progress}
                                  color={u.target_reached ? 'success' : u.target_progress >= 75 ? 'warning' : 'primary'}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </Box>
                              <Typography variant="caption" sx={{ minWidth: 36, fontWeight: 600 }}>
                                {u.target_progress}%
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">No target</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {u.bonus > 0 ? (
                            <Chip
                              size="small"
                              label={`$${u.bonus.toFixed(2)}`}
                              color="warning"
                              variant="outlined"
                              icon={<TrophyIcon />}
                              sx={{ fontWeight: 700 }}
                            />
                          ) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {u.overage > 0 ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="success.main">
                                ${u.overage_commission.toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                on ${u.overage.toLocaleString('en-AU', { minimumFractionDigits: 2 })} over
                              </Typography>
                            </Box>
                          ) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={u.total_commission > 0 ? 'success.main' : 'text.secondary'}
                          >
                            {u.total_commission > 0 ? `$${u.total_commission.toFixed(2)}` : '$0.00'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* ── Pending leave section ─────────────────────────────────────── */}
            {isAdmin && (
              <>
                <Typography variant="h6" gutterBottom>Pending Leave Approvals</Typography>
                {pendingLeaveLoading ? (
                  <CircularProgress size={24} />
                ) : pendingLeave.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>No pending leave requests.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Dates</TableCell>
                          <TableCell align="right">Hours</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingLeave.map(lr => (
                          <TableRow key={lr.id} hover>
                            <TableCell>{lr.employee_name}</TableCell>
                            <TableCell>{LEAVE_LABELS[lr.type] || lr.type}</TableCell>
                            <TableCell>
                              {new Date(lr.date_start).toLocaleDateString('en-AU')}
                              {lr.date_start !== lr.date_end && ` – ${new Date(lr.date_end).toLocaleDateString('en-AU')}`}
                            </TableCell>
                            <TableCell align="right">{lr.hours}</TableCell>
                            <TableCell sx={{ maxWidth: 200 }}>
                              <Typography variant="caption" noWrap>{lr.notes || '—'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Approve">
                                <IconButton size="small" color="success" onClick={() => handleLeaveAction(lr.id, 'approve')}>
                                  <ApproveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton size="small" color="error" onClick={() => handleLeaveAction(lr.id, 'reject')}>
                                  <RejectIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Divider sx={{ mb: 3 }} />
              </>
            )}

            {/* ── Xero Pay Run ──────────────────────────────────────────────── */}
            {isAdmin && (
              <>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>Submit to Xero Payroll</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
                  <Grid item xs={12} sm="auto">
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Pay Type</InputLabel>
                      <Select value={payType} label="Pay Type" onChange={e => setPayType(e.target.value)}>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm="auto">
                    <TextField
                      size="small"
                      type="date"
                      label="Period Start"
                      value={payPeriodStart}
                      onChange={e => setPayPeriodStart(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm="auto">
                    <TextField
                      size="small"
                      type="date"
                      label="Period End"
                      value={payPeriodEnd}
                      onChange={e => setPayPeriodEnd(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm="auto">
                    <Button
                      variant="outlined"
                      onClick={fetchPayPreview}
                      disabled={payPreviewLoading}
                      startIcon={payPreviewLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                    >
                      Preview Pay Run
                    </Button>
                  </Grid>
                </Grid>

                {/* Pay preview */}
                {payPreview && (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      Pay Run Preview — {payPreview.type === 'monthly' ? 'Monthly' : 'Weekly'} ({payPreview.periodStart} to {payPreview.periodEnd})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Employment Type</TableCell>
                            <TableCell align="right">Hours Worked</TableCell>
                            <TableCell align="right">Leave Hours</TableCell>
                            <TableCell align="right">Base Pay</TableCell>
                            <TableCell align="right">Commission</TableCell>
                            <TableCell align="right">Gross Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {payPreview.lines.map(line => (
                            <TableRow key={line.userId} hover>
                              <TableCell>{line.name}</TableCell>
                              <TableCell>
                                <Chip size="small" label={line.employment_type?.replace('_', ' ')} variant="outlined" />
                              </TableCell>
                              <TableCell align="right">{line.hoursWorked?.toFixed(1)}</TableCell>
                              <TableCell align="right">{line.leaveHours?.toFixed(1)}</TableCell>
                              <TableCell align="right">${line.basePay?.toFixed(2)}</TableCell>
                              <TableCell align="right">${line.commission?.toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>${line.grossTotal?.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell colSpan={6} align="right" sx={{ fontWeight: 700 }}>Total Gross</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              ${payPreview.totalGross?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                      <TextField
                        size="small"
                        type="date"
                        label="Payment Date"
                        value={paymentDate}
                        onChange={e => setPaymentDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SubmitIcon />}
                        onClick={() => setConfirmSubmitOpen(true)}
                      >
                        Submit Pay Run to Xero
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                  </>
                )}
              </>
            )}

            {/* ── Pay run history ───────────────────────────────────────────── */}
            <Typography variant="h6" gutterBottom>Pay Run History</Typography>
            {payHistoryLoading ? (
              <CircularProgress size={24} />
            ) : payHistory.length === 0 ? (
              <Alert severity="info">No pay runs submitted yet.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Total Gross</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payHistory.map(run => (
                      <TableRow key={run.id} hover>
                        <TableCell>
                          {new Date(run.period_start).toLocaleDateString('en-AU')} –{' '}
                          {new Date(run.period_end).toLocaleDateString('en-AU')}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={run.type} variant="outlined" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ${parseFloat(run.total_gross).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={run.status}
                            color={run.status === 'SUBMITTED' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {run.submitted_at ? new Date(run.submitted_at).toLocaleDateString('en-AU') : '—'}
                        </TableCell>
                        <TableCell>{run.submitted_by_name || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* ── Unpaid Orders Tab ────────────────────────────────────────────── */}
        <TabPanel value={tab} index={2}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Ad orders with status <strong>Invoiced</strong> or <strong>Uploaded</strong> awaiting payment.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={unpaidLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={fetchUnpaidOrders}
                disabled={unpaidLoading}
                sx={{ ml: 'auto' }}
              >
                Refresh
              </Button>
            </Box>

            {unpaidLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Order #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sales Person</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Amount (inc GST)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Invoice Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unpaidOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No unpaid orders — all clear!
                        </TableCell>
                      </TableRow>
                    ) : unpaidOrders.map(order => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>#{order.id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{order.orgName || '—'}</Typography>
                          {order.primaryEmail && (
                            <Typography variant="caption" color="text.secondary">{order.primaryEmail}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.salesPersonName || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.invoiceNumber
                              ? order.invoiceNumber.substring(0, 8) + '…'
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            ${parseFloat(order.totalPrice || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.invoiceDate
                              ? new Date(order.invoiceDate).toLocaleDateString('en-AU')
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={order.dueDate && new Date(order.dueDate) < new Date()
                              ? { color: 'error.main', fontWeight: 600 }
                              : {}}
                          >
                            {order.dueDate
                              ? new Date(order.dueDate).toLocaleDateString('en-AU')
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={order.status}
                            color={order.status?.toLowerCase() === 'invoiced' ? 'warning' : 'info'}
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {order.xero_invoice_url && (
                              <Tooltip title="View Invoice">
                                <IconButton size="small" component="a" href={order.xero_invoice_url} target="_blank" rel="noopener noreferrer">
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Invoice Notes">
                              <IconButton size="small" color="primary" onClick={() => openNotesDialog(order.id, 'ad', order.orgName || `Order #${order.id}`)}>
                                <NoteAltIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Payment Plan">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => setPaymentPlanConfirm({ open: true, order })}
                              >
                                <PaymentPlanIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as Paid">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setUnpaidConfirm({ open: true, order, action: 'paid' })}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Order">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setUnpaidConfirm({ open: true, order, action: 'cancel' })}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* ── Unpaid Orders: mark paid / cancel confirmation ─────────────────── */}
      <Dialog open={unpaidConfirm.open} onClose={() => setUnpaidConfirm({ open: false, order: null, action: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          {unpaidConfirm.action === 'paid' ? 'Mark Order as Paid' : 'Cancel Order'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {unpaidConfirm.action === 'paid'
              ? `Mark order #${unpaidConfirm.order?.id} (${unpaidConfirm.order?.orgName}) as paid?`
              : `Cancel order #${unpaidConfirm.order?.id} (${unpaidConfirm.order?.orgName})? This cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpaidConfirm({ open: false, order: null, action: null })}>Back</Button>
          <Button
            variant="contained"
            color={unpaidConfirm.action === 'paid' ? 'success' : 'error'}
            onClick={handleUnpaidAction}
          >
            {unpaidConfirm.action === 'paid' ? 'Mark Paid' : 'Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Payment Plan confirmation ──────────────────────────────────────── */}
      <Dialog open={paymentPlanConfirm.open} onClose={() => setPaymentPlanConfirm({ open: false, order: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Set Payment Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Mark order <strong>#{paymentPlanConfirm.order?.id}</strong> ({paymentPlanConfirm.order?.orgName}) as being on a
            Payment Plan? The invoice will remain open in Xero but the status in the CRM will show as Payment Plan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentPlanConfirm({ open: false, order: null })}>Back</Button>
          <Button variant="contained" color="info" startIcon={<PaymentPlanIcon />} onClick={handlePaymentPlan}>
            Set Payment Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Action dialog (void / credit note / dispute) ──────────────────── */}
      <Dialog open={actionDialog.open} onClose={closeAction} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog.type === 'void' && 'Void Invoice'}
          {actionDialog.type === 'credit' && 'Issue Credit Note'}
          {actionDialog.type === 'dispute' && 'Flag as Disputed'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {actionDialog.type === 'void' && 'This will void the invoice in Xero. This action cannot be undone.'}
            {actionDialog.type === 'credit' && 'This will create a credit note in Xero for the full invoice amount.'}
            {actionDialog.type === 'dispute' && 'Flag this invoice as disputed and enter the reason.'}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Reason"
            multiline
            rows={3}
            value={actionReason}
            onChange={e => setActionReason(e.target.value)}
            required={actionDialog.type === 'dispute'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction}>Cancel</Button>
          <Button
            variant="contained"
            color={actionDialog.type === 'void' ? 'error' : 'primary'}
            onClick={submitAction}
            disabled={actionDialog.type === 'dispute' && !actionReason}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Submit pay run confirmation ───────────────────────────────────── */}
      <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)}>
        <DialogTitle>Submit Pay Run to Xero</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will submit a pay run of <strong>${payPreview?.totalGross?.toFixed(2)}</strong> to Xero Payroll.
            Xero will calculate PAYG tax, superannuation, and process STP lodgement. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmitOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitPayRun}
            disabled={submitLoading}
            startIcon={submitLoading ? <CircularProgress size={16} /> : <SubmitIcon />}
          >
            Submit to Xero
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Invoice Notes dialog ─────────────────────────────────────────── */}
      <OrderWorkflowNotesDialog
        open={notesDialog.open}
        onClose={closeNotesDialog}
        title={notesDialog.title}
        entityType={notesDialog.entityType}
        entityId={notesDialog.entityId}
        initialNotes={notesDialog.notes}
        onNotesUpdate={(updatedNotes) => setNotesDialog(prev => ({ ...prev, notes: updatedNotes }))}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

function getMonday() {
  const d = new Date();
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}

function getSunday() {
  const d = new Date();
  const day = d.getDay();
  const diff = (day === 0 ? 0 : 7 - day);
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}
